import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WarningOctagon, SignOut, Info } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';

/**
 * Sticky top banner that pins to the top of the viewport whenever the current
 * session is a super-admin impersonating a tenant user. Purposefully loud
 * (bright red, high contrast, non-dismissable) — the whole point is that the
 * admin can never lose track of "I'm inside someone else's account, be
 * careful before typing."
 *
 * Layout renders this ONCE at the very top; everything below just gets
 * pushed down by the banner's natural height. Absolutely no `position:fixed`
 * so it doesn't overlap sticky headers deeper in the app.
 */
const ImpersonationBanner = () => {
  const { user, exitImpersonation } = useAuthStore();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  // Only render when we're actually impersonating. The JWT bleeds
  // `impersonated_by` into /auth/me so this string is populated in-session.
  if (!user?.impersonated_by) return null;

  const handleExit = async () => {
    setExiting(true);
    const r = await exitImpersonation();
    if (r?.needsRelogin) {
      // Admin token expired during impersonation — send them back to the
      // secure entry point, not the tenant login.
      navigate('/vooxy-console', { replace: true });
    } else {
      navigate('/super-admin', { replace: true });
    }
  };

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      className="w-full bg-gradient-to-r from-red-600 via-red-700 to-red-600 text-white shadow-lg border-b-4 border-red-900"
      style={{ fontFamily: 'Outfit, sans-serif' }}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <WarningOctagon size={22} weight="fill" className="shrink-0 animate-pulse" />
          <div className="min-w-0">
            <div className="text-sm font-bold uppercase tracking-widest leading-tight">
              Support Mode · STOP EDITING unless required
            </div>
            <div className="text-xs opacity-90 truncate">
              Logged in as{' '}
              <b data-testid="impersonation-target-name">{user.name}</b>{' '}
              (<span data-testid="impersonation-target-email">{user.email}</span>)
              · impersonated by{' '}
              <b data-testid="impersonation-actor-email">{user.impersonated_by}</b>
              · every write is audit-logged
            </div>
          </div>
        </div>
        <button
          onClick={handleExit}
          disabled={exiting}
          data-testid="exit-impersonation-btn"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-1.5 rounded-md bg-white text-red-700 font-bold text-xs uppercase tracking-wider hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed transition shadow"
        >
          <SignOut size={14} weight="bold" />
          {exiting ? 'Exiting…' : 'Exit impersonation'}
        </button>
      </div>
      {/* Compact info strip on hover — extra context without cluttering the main bar */}
      <div className="max-w-7xl mx-auto px-4 pb-1.5 hidden sm:flex items-center gap-1.5 text-[10px] text-red-100/80">
        <Info size={11} />
        Your keystrokes are their reality — quotations approved / customers created
        will be attributed to <b>{user.name}</b> but marked with your admin email in
        the tenant&apos;s audit log.
      </div>
    </div>
  );
};

export default ImpersonationBanner;
